import React from 'react';
import { LucideIcon, FileJson, Workflow, Inbox, AlertCircle } from 'lucide-react';

interface EmptyStateProps {
  /** Icon to display */
  icon?: LucideIcon;
  /** Title of the empty state */
  title: string;
  /** Description text */
  description: string;
  /** Call-to-action button */
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  /** Additional content below description */
  children?: React.ReactNode;
  /** Custom icon emoji (alternative to Lucide icon) */
  emoji?: string;
}

/**
 * Generic empty state component
 * Used for "no data" scenarios with helpful guidance
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
  children,
  emoji,
}) => {
  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <div className="text-center max-w-md">
        {/* Icon or Emoji */}
        <div className="flex justify-center mb-6">
          {emoji ? (
            <div className="text-6xl">{emoji}</div>
          ) : Icon ? (
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center">
              <Icon className="w-10 h-10 text-muted-foreground" />
            </div>
          ) : null}
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-3 text-foreground">
          {title}
        </h2>

        {/* Description */}
        <p className="text-muted-foreground mb-6">
          {description}
        </p>

        {/* Additional Content */}
        {children}

        {/* Action Button */}
        {action && (
          <button
            onClick={action.onClick}
            className={`
              px-6 py-3 rounded-lg font-medium transition-colors
              ${action.variant === 'secondary'
                ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }
            `}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Empty state shown when no file has been uploaded
 */
export const NoFileEmptyState: React.FC<{ onUploadClick: () => void }> = ({ onUploadClick }) => {
  return (
    <EmptyState
      icon={FileJson}
      title="No Workflow Loaded"
      description="Upload a Zapier export file to visualize and analyze your workflows"
      action={{
        label: 'Upload File',
        onClick: onUploadClick,
      }}
    >
      <div className="mb-6 p-4 bg-muted/50 rounded-lg text-left">
        <h3 className="text-sm font-semibold mb-2 text-foreground">How to export from Zapier:</h3>
        <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
          <li>Go to your Zapier dashboard</li>
          <li>Request your data export (GDPR)</li>
          <li>Download the JSON file</li>
          <li>Upload it here</li>
        </ol>
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        🔒 Your data never leaves your browser - 100% private
      </p>
    </EmptyState>
  );
};

/**
 * Empty state shown when file has no workflows
 */
export const NoWorkflowsEmptyState: React.FC<{ onUploadAnother: () => void }> = ({
  onUploadAnother,
}) => {
  return (
    <EmptyState
      icon={Workflow}
      title="No Workflows Found"
      description="This file doesn't contain any Zapier workflows. Make sure you've uploaded a valid Zapier export file."
      action={{
        label: 'Try Another File',
        onClick: onUploadAnother,
      }}
    >
      <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg text-left">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900 dark:text-amber-100 mb-1">
              What went wrong?
            </p>
            <ul className="text-amber-700 dark:text-amber-300 space-y-1 list-disc list-inside">
              <li>The file may be corrupted</li>
              <li>You may have uploaded the wrong file</li>
              <li>The export may not have completed</li>
            </ul>
          </div>
        </div>
      </div>
    </EmptyState>
  );
};

/**
 * Empty state shown when workflow has no nodes
 */
export const NoNodesEmptyState: React.FC = () => {
  return (
    <EmptyState
      emoji="🤔"
      title="Empty Workflow"
      description="This workflow doesn't have any steps. It may be a draft or incomplete workflow."
    >
      <p className="text-sm text-muted-foreground mt-4">
        Try selecting a different workflow from the dropdown above.
      </p>
    </EmptyState>
  );
};

/**
 * Empty state shown when no node is selected
 */
export const NoNodeSelectedEmptyState: React.FC = () => {
  return (
    <div className="flex items-center justify-center h-full p-8">
      <div className="text-center max-w-sm">
        <div className="mb-4">
          <Inbox className="w-16 h-16 mx-auto text-muted-foreground opacity-50" />
        </div>
        <h3 className="text-lg font-semibold mb-2 text-foreground">
          No Step Selected
        </h3>
        <p className="text-sm text-muted-foreground">
          Click on a step in the workflow to view its configuration details
        </p>
      </div>
    </div>
  );
};

/**
 * Empty state shown when node has no configuration data
 */
export const NoConfigDataEmptyState: React.FC = () => {
  return (
    <div className="p-6">
      <EmptyState
        emoji="📭"
        title="No Configuration"
        description="This step doesn't have any configuration data to display."
      />
    </div>
  );
};
